import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { ACTIONS } from "../../common/Constants";
import { ACTIVITY_TYPE, ACTIVITY_TYPE_ICONS, API_DELIMITER, API_RESULT, ERROR_MESSAGES } from "../../../utils/constants";
import { useDeleteComplianceSchedule, useGetAllComplianceActivities } from "../../../backend/compliance";
import Icon from "../../common/Icon";
import Table, { CellTmpl, DEFAULT_PAYLOAD, TitleTmpl, reactFormatter } from "../../common/Table";
import { preventDefault } from "../../../utils/common";
import AlertModal from "../../common/AlertModal";
import ConfirmModal from "../../common/ConfirmModal";
import PageLoader from "../../shared/PageLoader";
import { hasUserAccess } from "../../../backend/auth";
import { USER_PRIVILEGES } from "../UserManagement/Roles/RoleConfiguration";
import ComplianceAssignUser from "./ComplianceAssignUser";
import BulkAssignModal from "./BulkAssignModal";

import MastersLayout from "../Masters/MastersLayout";
import { GetComplianceBreadcrumb } from "./Compliance.constants";
import { COMPLIANCE_ACTIVITY_INDICATION, ComplianceActivityStatus, ComplianceStatusMapping } from "../../../constants/Compliance.constants";
import ComplianceScheduleAdvanceFilter from "./ComplianceScheduleAdvanceFilter";
import TableActions, { ActionButton } from "../../common/TableActions";
import OptionalLocations from "../../common/OptionalLocations";
import { AxiosError } from "axios";

const SortFields: any = {
    'act.name': 'act_name',
    'rule.name': 'rule_name',
    'activity.name': 'activity_name',
    'associate_company.name': 'associate_company_name',
    'location.name': 'location_name',
    'activity.activity_type': 'activity_activity_type',
    'veritical.name': 'vertical_name',
    'department.name': 'department_name',
    'compliance_owner.name': 'compliance_owner_name',
    'compliance_manager.name': 'compliance_manager_name'
};

const INCLUDE_ASSOCIATION_COLUMNS = {
    act: { only: ['name'] },
    rule: { only: ['name'] },
    activity: { only: ['name', 'activity_type'] },
    vertical: { only: ['name'] },
    department: { only: ['name'] },
    compliance_owner: { only: ['name'] },
    compliance_manager: { only: ['name'] },
    company: { only: ['name', 'id'] }
};

const INCLUDE_ASSOCIATIONS = ['act', 'rule', 'activity', 'vertical', 'department', 'compliance_owner', 'compliance_manager', 'company'];

function ComplianceScheduleDetails(this: any) {
    const [breadcrumb] = useState(GetComplianceBreadcrumb('Compliance Schedule Details'));
    const [activity, setActivity] = useState<any>();
    const [action, setAction] = useState(ACTIONS.NONE);
    const [data, setData] = useState<any>();
    const [params, setParams] = useState<any>();
    const [locationFilters, setLocationFilter] = useState<any>();
    const lfRef: any = useRef();
    lfRef.current = locationFilters;
    const [advaceSearchFilters, setAdvanceSearchFilters] = useState<any>();
    const afRef: any = useRef();
    afRef.current = advaceSearchFilters;
    const [statusFilters] = useState({ 'status_in': [ComplianceActivityStatus.DUE, ComplianceActivityStatus.NON_COMPLIANT] });
    const sfRef: any = useRef();
    sfRef.current = statusFilters;
    const [payload, setPayload] = useState<any>();
    const payloadRef: any = useRef();
    payloadRef.current = payload;
    const { activities, total, isFetching, refetch } = useGetAllComplianceActivities(payload, Boolean(payload));
    const [showBulkAssign, setShowBulkAssign] = useState(false);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [alertMessage, setAlertMessage] = useState<any>(null);
    const [deleteComplianceRequests, setDeleteComplianceRequests] = useState<any[]>([]);
    const [deleteCount, setDeleteCount] = useState<number>(0);
    const [complianceDeleting, setComplianceDeleting] = useState<boolean>(false);
    const [delectingAction, setDeletingAction] = useState(ACTIONS.NONE);
    const { deleteComplianceSchedule, deleting } = useDeleteComplianceSchedule((response: any) => {
        if (response instanceof AxiosError){
            const data = (response.response || {}).data;
            const error = (data || {}).error || ERROR_MESSAGES.DEFAULT;
            setComplianceDeleting(false);
            toast.error(error);
        } else {
            if(delectingAction == ACTIONS.BULK_DELETE){
                setDeleteCount(deleteCount + 1);
            } else if(delectingAction == ACTIONS.DELETE) {
                toast.success(`Activity deleted successfully.`);
                setComplianceDeleting(false);
                setDeleteCount(0);
                setDeleteComplianceRequests([]);
                setDeletingAction(ACTIONS.NONE);
                dismissAction();
                refetch();
            }
        }
    });

    const buttons: ActionButton[] = [
        {
            label: 'Assign User',
            name: 'assignUser',
            icon: 'people',
            disabled: !(selectedRows || []).length,
            action: handleAssignUser,
            privilege: USER_PRIVILEGES.ASSIGN_COMPLIANCE_SCHEDULE_DETAILS
        }, {
            label: 'Bulk Assign',          
            name: 'bulkAssign',
            icon: 'people',
            disabled: !(payload && payload.filters && payload.filters.company_id_eq),
            action: handleBulkAssign,
            privilege: USER_PRIVILEGES.ASSIGN_COMPLIANCE_SCHEDULE_DETAILS
        }, {
            label: 'Bulk Delete',
            name: 'bulkDelete',
            icon: 'trash',
            disabled: !(selectedRows || []).length,
            action: handleBulkDelete,
            privilege: USER_PRIVILEGES.DELETE_COMPLIANCE_SCHEDULE_DETAILS
        }
    ];

    function hasFilters(ref: any, field = 'company_id') {
        const _filters = (ref ? ref.current : { ...(payloadRef.current || {}) }.filters) || [];
        const company = _filters.find((x: any) => x.field === field);
        return (company || {}).value;
    }

    function onLocationChange(event: any) {
        const keys = Object.keys(event);
        const filters: any = {};
        keys.forEach((key) => {
            filters[key] = event[key];
        })
        setLocationFilter(filters);
    }

    function search(event: any) {
        const _filters: any = {};
        const keys = Object.keys(event || {});
        keys.forEach(key => {
            _filters[key] = event[key];
        });

        setAdvanceSearchFilters(_filters);
    }

    function dismissAction() {
        setAction(ACTIONS.NONE);
        setActivity(null);
    }

    function MonthTmpl({ cell }: any) {
        const row = cell.getData();
        return (
            <div className="ellipse two-lines">{row.month} ({row.year})</div>
        )
    }

    function DueDateTmpl({ cell }: any) {
        const value = cell.getValue();
        return (
            <span className="text-warn" >{dayjs(value).format('DD-MM-YYYY')}</span>
        )
    }

    function FormStatusTmpl({ cell }: any) {
        const status = cell.getValue();
        return (
            <div className="d-flex align-items-center position-relative">
                <span className="ellipse" style={{ color: COMPLIANCE_ACTIVITY_INDICATION[status] || '' }}>{ComplianceStatusMapping[status] || status}</span>
            </div>
        )
    }

    function ActionColumnElements({ cell }: any) {
        const row = cell.getData();

        return (
            <div className="d-flex flex-row align-items-center position-relative">
                {
                    hasUserAccess(USER_PRIVILEGES.ASSIGN_COMPLIANCE_SCHEDULE_DETAILS) &&
                    <Icon className="me-3" type="button" name="people" text="Assign User" data={row} action={handleSingleAssign} />
                }
                {
                    hasUserAccess(USER_PRIVILEGES.DELETE_COMPLIANCE_SCHEDULE_DETAILS) &&
                    <Icon type="button" name="trash" text="Delete" data={row} action={handleDelete} />
                }
            </div>
        )
    }

    function ActivityTypeTmpl({ cell }: any) {
        const value = cell.getValue() || ACTIVITY_TYPE.AUDIT;
        return (
            <div className="d-flex flex-row align-items-center justify-content-center position-relative">
                <Icon name={ACTIVITY_TYPE_ICONS[value]} text={value} />
            </div>
        )
    }

    const columns = [
        {
            title: "Month (Year)", field: "month", width: 100,
            formatter: reactFormatter(<MonthTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Act", field: "act.name", widthGrow: 2,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Rule", field: "rule.name", widthGrow: 2,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Activity", field: "activity.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 2
        },
        {
            title: "Activity Type", field: "activity.activity_type",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            minWidth: 140
        },
        {
            title: "Vertical", field: "vertical.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Department", field: "department.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Owner", field: "compliance_owner.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Manager", field: "compliance_manager.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Due Date", field: "due_date", width: 120,
            formatter: reactFormatter(<DueDateTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Evidence Status", field: "status", width: 160,
            formatter: reactFormatter(<FormStatusTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Actions", hozAlign: "center", width: 120,
            headerSort: false,
            formatter: reactFormatter(<ActionColumnElements />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        }
    ];

    function rowFormatter(row: any) {
        const data = row.getData();
        const element = row.getElement();
        if (data.is_notice) {
            element.classList.add('notice-activity');
        }
    }

    const [tableConfig] = useState({
        paginationMode: 'remote',
        ajaxRequestFunc,
        columns,
        rowHeight: 'auto',
        selectable: true,
        paginate: true,
        initialSort: ['month desc'],
        rowFormatter
    });

    function formatApiResponse(params: any, list: any[], totalRecords: number) {
        const { page, per } = params || {};
        const tdata = {
            data: list,
            total: totalRecords,
            last_page: Math.ceil(totalRecords / (per || 1)) || 1,
            page: page || 1
        };
        setData(tdata);
        return tdata;
    }

    function ajaxRequestFunc(url: any, config: any, params: any) {
        const sortParams = (params.sort || []).map((x: any) => `${SortFields[x.field] || x.field} ${x.dir || 'desc'}`)
        console.log(sortParams);
        const _params = {
            page: params.page,
            per: params.size,
            sorts: sortParams,
            includes: INCLUDE_ASSOCIATIONS,
            include: INCLUDE_ASSOCIATION_COLUMNS
        };
        setParams(_params);
        setPayload({
            ...DEFAULT_PAYLOAD,
            ..._params,
            filters: {
                ...(lfRef.current || {}),
                ...(afRef.current || {}),
                ...(sfRef.current || {})
            }
        });
        return Promise.resolve(formatApiResponse(params, activities, total));
    }

    function handlePageNav(_pagination: any) {
        const _params = { ..._pagination };
        setParams({ ..._params });
        setPayload({ ...payload, ..._params })
    }

    function getAdvanceSearchPayload() {
        const _filters = [...(afRef.current || [])];
        const _payload: any = {};
        _filters.forEach(x => {
            _payload[x.columnName] = x.value;
        });
        return _payload;
    }

    function handleSingleAssign(_activity: any) {
        setActivity(_activity)
        setAction(ACTIONS.ASSIGN_SINGLE);
    }

    function handleDelete(_activity: any) {
        setActivity(_activity)
        setAction(ACTIONS.DELETE);
        setDeletingAction(ACTIONS.DELETE);
    }

    function handleCancel(refresh = false) {
        setAction(ACTIONS.NONE);
        if (refresh) {
            refetch();
        }
    }

    function performDelete() {
        deleteComplianceSchedule({id: activity.id});
        setComplianceDeleting(true);
    }
    function performBulkDelete() {
        const requests = selectedRows.map((x: any) => x.id);
        const id = requests.shift();
        setDeleteComplianceRequests(requests);
        setComplianceDeleting(true);
        deleteComplianceSchedule({id: id});
    }
    useEffect(() => {
        if (delectingAction == ACTIONS.BULK_DELETE && deleteCount != selectedRows.length) {
            const tempRequests = [...deleteComplianceRequests];
            const id = tempRequests.shift();
            setDeleteComplianceRequests(tempRequests);
            deleteComplianceSchedule({id: id});
        } else if (delectingAction == ACTIONS.BULK_DELETE && deleteCount === selectedRows.length) {
            toast.success(`Activities deleted successfully.`);
            setComplianceDeleting(false);
            setDeleteCount(0);
            setDeleteComplianceRequests([]);
            setDeletingAction(ACTIONS.NONE);
            dismissAction();
            refetch();
        }
    }, [deleteCount]);

    function handleBulkDelete() {
        if ((selectedRows || []).length > 0) {
            setAction(ACTIONS.BULK_DELETE);
            setDeletingAction(ACTIONS.BULK_DELETE);
        }
    }

    function handleAssignUser() {
        if ((selectedRows || []).length > 0) {
            setAction(ACTIONS.ASSIGN_BULK);
        }
    }

    function handleBulkAssign() {
        setShowBulkAssign(true);
    }

    function handleCopyTo() {
        if ((selectedRows || []).length > 0) {
            setAction(ACTIONS.COPY_TO);
        }
    }

    function onSelectionChange(_selectedRows: any) {
        setSelectedRows(_selectedRows);
    }

    useEffect(() => {
        if (locationFilters) {
            setPayload({
                ...payload,
                ...DEFAULT_PAYLOAD,
                sorts: [`month desc`],
                ...params,
                filters: {
                    ...(locationFilters || {}),
                    ...(afRef.current || {}),
                    ...(sfRef.current || {})
                }
            });
        }
    }, [locationFilters]);

    useEffect(() => {
        if (advaceSearchFilters) {
            setPayload({
                ...payload,
                ...DEFAULT_PAYLOAD,
                sorts: [`month desc`],
                ...params,
                filters: {
                    ...(lfRef.current || {}),
                    ...advaceSearchFilters,
                    ...(sfRef.current || {})
                }
            });
        }
    }, [advaceSearchFilters]);

    useEffect(() => {
        if (!isFetching && payload) {
            setSelectedRows([]);
            setData(formatApiResponse(params, activities, total));
        }
    }, [isFetching])

    return (
        <>
            <MastersLayout title="Compliance Schedule Details" breadcrumbs={breadcrumb}>
                <form className="p-0 mx-3 my-2">
                    <div className="card shadow border-0 p-2 mt-2 mb-3 filters">
                        <div className="d-flex flex-row m-0 align-items-end">
                            <OptionalLocations onChange={onLocationChange} />
                            <div>
                                <ComplianceScheduleAdvanceFilter onChange={search} />
                            </div>
                            <TableActions buttons={buttons} />
                        </div>
                    </div>
                </form>
                <Table data={data} options={tableConfig} isLoading={isFetching} onSelectionChange={onSelectionChange.bind(this)} onPageNav={handlePageNav} />
            </MastersLayout>
            {
                !!alertMessage &&
                <AlertModal message={alertMessage} onClose={(e: any) => {
                    preventDefault(e);
                    setAlertMessage(null);
                }} />
            }
            {
                action === ACTIONS.DELETE &&
                <ConfirmModal title={'Delete Activity'} onSubmit={performDelete} onClose={() => setAction(ACTIONS.NONE)}>
                    <div className="text-center mb-4">Are you sure you want to delete the activity ?</div>
                </ConfirmModal>
            }
            {
                action === ACTIONS.BULK_DELETE &&
                <ConfirmModal title={'Delete Activities'} onSubmit={performBulkDelete} onClose={() => setAction(ACTIONS.NONE)}>
                    <p className="text-center mb-4">There are {selectedRows.length} record(s) selected for deleting.</p>
                    <div className="text-center mb-4">Are you sure you want to delete all of them ?</div>
                </ConfirmModal>
            }
            {
                (action === ACTIONS.ASSIGN_BULK || action === ACTIONS.ASSIGN_SINGLE) &&
                <ComplianceAssignUser action={action}
                    activities={action === ACTIONS.ASSIGN_BULK ? selectedRows : [activity]} onCancel={handleCancel} />
            }
            {
                showBulkAssign &&
                <BulkAssignModal
                    companyId={payload && payload.filters ? payload.filters.company_id_eq : undefined}
                    pageFilters={payload ? payload.filters : {}}
                    total={total}
                    onCancel={() => setShowBulkAssign(false)}
                />
            }

            {complianceDeleting && <PageLoader message={delectingAction === ACTIONS.BULK_DELETE ? `Deleting ${deleteCount + 1} of ${selectedRows.length}` : 'Deleting...'} />}
        </>
    );

}

export default ComplianceScheduleDetails;